import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { comnEnvs, comnUtils } from "@/comn/utils";
import { Button, Grid, Group, Layout, Page } from "@/comn/components";
import { useFetch, useForm, useGrid, useStore, useToast, useResource, useModal, usePage } from "@/comn/hooks";
import {
    APIS,
    SF_BIDR_APRV_SRCH,
    SG_BIDR_APRV_LIST,
    URLS,
} from "@/tra/tancis/ptli/usr/bidraprv/services/PtliBidrAprvService";

/*
 * Bidder Approval Request List
 * !== 공매자 승인 요청 목록 ==!
 */
export const PTLI0920001Q = (): JSX.Element => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0015", "auth_ptlianyADM"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal();
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!

    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "COM0011" },
            { area: "comnCd", comnCd: "CGM0095" },
            { area: "comnCd", comnCd: "CGM0096" },
        ],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Bidder Approval Request List Search !== 공매자 사용자 목록 검색 ==!
        bidrAprvRqstListSrch: useForm({
            defaultSchema: SF_BIDR_APRV_SRCH,
            defaultValues: { ...pgeStore?.form } || {},
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        // Bidder Approval Request List !== 공매자 사용자 목록 ==!
        bidrAprvRqstList: useGrid({
            defaultSchema: SG_BIDR_APRV_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        //Check Bidder Approval Request List !==  공매자 사용자 목록 조회==!
        getCoAprvRqstList: useFetch({
            api: (page = grid.bidrAprvRqstList.page) =>
                APIS.getBidrAprvRqstList(form.bidrAprvRqstListSrch.getValues(), page, grid.bidrAprvRqstList.size),
            enabled: comnUtils.isEmpty(form.bidrAprvRqstListSrch.errors),
            key: [grid.bidrAprvRqstList.page, grid.bidrAprvRqstList.size],
            onSuccess: () => {
                setStore(pgeUid, {
                    form: form.bidrAprvRqstListSrch.getValues(),
                    page: grid.bidrAprvRqstList.page,
                    size: grid.bidrAprvRqstList.size,
                });
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00025" }), // Failed to select. !== 조회가 실패하였습니다. ==!
            showToast: true,
        }),
    };
    const render = {
        cell: {
            usrNm: (props: any) => {
                const { rowValues, value } = props;
                return <Link to={`/ptli/usr/bidraprv/ptli0920002s/${rowValues.xtrnUsrAplyId}`}>{value}</Link>;
            },
        },
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Click Search Button !== 검색버튼 ==!
        onSrchBtnClick: (page: number = 0) => {
            grid.bidrAprvRqstList.setPage(page);
            fetch.getCoAprvRqstList.fetch(page);
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        handler.onSrchBtnClick(pgeStore?.page);
    }, []);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.bidrAprvRqstListSrch.schema.xtrnUsrIdntfNo} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.bidrAprvRqstListSrch.schema.usrNm} />
                            <Group.Control {...form.bidrAprvRqstListSrch.schema.bsopPrcssStat} />
                        </Group.Row>
                    </Group.Section>
                    <Layout>
                        <Layout.Left>
                            <Button role={"reset"} onClick={() => form.bidrAprvRqstListSrch.reset()} />
                        </Layout.Left>
                        <Layout.Right>
                            <Button
                                role={"search"}
                                onClick={() => {
                                    handler.onSrchBtnClick();
                                }}
                            />
                        </Layout.Right>
                    </Layout>
                </Group.Body>
            </Group>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Grid
                            {...grid.bidrAprvRqstList.grid}
                            data={fetch.getCoAprvRqstList.data?.bidrAprvRqstList}
                            render={render}
                        />
                    </Group.Section>
                </Group.Body>
            </Group>
        </Page>
    );
};
